import { useState, useEffect, useRef, useCallback } from 'react';
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import { useSwipeable } from 'react-swipeable';
import styles from './styles/gallery.module.css';
import slidesData from '../../../data/Homepage/Slides.json';

import { logGalleryImageView, logGalleryCategoryChange } from "../../../helpers/google-analytics/gaEvents";

const Gallery = () => {
  const [galleryData, setGalleryData] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('kitchens');
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null); // Reference for the interval
  const [fade, setFade] = useState(false);

  // Initialize gallery data and category
  useEffect(() => {
    setGalleryData(slidesData);
    if (!slidesData[selectedCategory]) {
      setSelectedCategory(Object.keys(slidesData)[0]);
    }
  }, [selectedCategory]);

  const images = galleryData[selectedCategory] || [];
  const categories = Object.keys(galleryData);

  // Start the interval
  const startInterval = useCallback(() => {
    if (intervalRef.current) return; // Prevent multiple intervals from being created

    intervalRef.current = setInterval(() => {
      setFade(true);
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
        setFade(false);
      }, 300); // match fade duration
    }, 4000);
  }, [images.length]);

  // Clear and restart interval
  const restartInterval = () => {
    clearInterval(intervalRef.current); // Clear the existing interval
    intervalRef.current = null; // Reset the intervalRef to allow a new interval to be set
    startInterval(); // Start a new interval
  };

  // Change image with fade effect and restart interval
  const changeImage = (newIndex) => {
    setFade(true);
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setFade(false);
      restartInterval(); // Restart the interval after image change
    }, 300); // Fade duration
  };

  // Handle category selection
  const handleCategoryClick = (cat) => {
    logGalleryCategoryChange();
    setSelectedCategory(cat);
    setCurrentIndex(0);
    restartInterval(); // Restart the interval on category change
  };

  // Manage auto slideshow effect
  useEffect(() => {
    if (images.length > 0) {
      startInterval(); // Start the interval on initial load
    }

    // Clean up interval when component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [selectedCategory, images.length, startInterval]);

  // Image navigation logic
  const prevImage = () => {
    logGalleryImageView();
    const newIndex = (currentIndex - 1 + images.length) % images.length;
    changeImage(newIndex);
  };

  const nextImage = () => {
    logGalleryImageView();
    const newIndex = (currentIndex + 1) % images.length;
    changeImage(newIndex);
  };

  // Swipe handlers for mobile
  const handlers = useSwipeable({
    onSwipedLeft: () => nextImage(),
    onSwipedRight: () => prevImage(),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const containerRef = useRef(null);
  const firstImageRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(null);

  // On first image load, fix container height
  const handleFirstImageLoad = () => {
    if (firstImageRef.current && containerRef.current && !containerHeight) {
      const height = firstImageRef.current.naturalHeight / firstImageRef.current.naturalWidth * containerRef.current.offsetWidth;
      setContainerHeight(height);
    }
  };

  // Styles
  const arrowLeft = `${styles.arrow} ${styles.left}`;
  const arrowRight = `${styles.arrow} ${styles.right}`;
  const slideImage = `${styles.slideImage} ${fade ? styles.fade : ''}`;

  return (
    <section className={styles.galleryContainer}>
      <nav className={styles.gallerySidebar} aria-label="Gallery Categories">
        <h2>Our Work</h2>
        <ul>
          {categories.map((cat) => (
            <li
              key={cat}
              className={cat === selectedCategory ? styles.active : ''}
              onClick={() => handleCategoryClick(cat)}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCategoryClick(cat); }}
              aria-current={cat === selectedCategory ? 'true' : undefined}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </li>
          ))}
        </ul>
      </nav>

      <div
        {...handlers}
        className={styles.gallerySlideshow}
        role="region"
        aria-label="Image gallery slideshow"
        ref={containerRef}
        style={ containerHeight ? { height: containerHeight + 'px' } : undefined }
      >
        <button
          className={arrowLeft}
          onClick={prevImage}
          aria-label="Previous image"
          tabIndex={0}
        >
          <FaChevronLeft size={30} />
        </button>

        {images.length > 0 && (
          <img
            key={currentIndex}
            src={images[currentIndex].src}
            alt={images[currentIndex].alt}
            className={slideImage}
            draggable={false}            
            loading="lazy"
            ref={currentIndex === 0 ? firstImageRef : null}
            onLoad={currentIndex === 0 ? handleFirstImageLoad : undefined}
          />
        )}

        <button
          className={arrowRight}
          onClick={nextImage}
          aria-label="Next image"
          tabIndex={0}
        >
          <FaChevronRight size={30} />
        </button>

        <div
          className={styles.slideCounter}
          aria-live="polite"
          aria-atomic="true"
        >
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </section>
  );
};

export default Gallery;
